import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";

import {
  RiskManagementOverallRiskBarChartsDocument,
  type RiskManagementOverallRiskBarChartsQuery,
  type RiskManagementOverallRiskBarChartsQueryVariables,
} from "$/__generated__/graphql";

import {
  convertPostMitigationBarDataToSeries,
  appendPostMitigationRiskTooltipExtras,
  appendPostMitigationSubgroups,
} from "./utils";
import { useGlobalFiltersContext } from "contexts/filters/context";
import {
  GeneralBarDatum,
  storageFormatters,
} from "@jana-technology/platform-ui";

export const useMitigationRiskCharts = (
  options: useQuery.Options<
    RiskManagementOverallRiskBarChartsQuery,
    RiskManagementOverallRiskBarChartsQueryVariables
  >,
) => {
  const overallRiskChartsQueryResult = useQuery(
    RiskManagementOverallRiskBarChartsDocument,
    options,
  );

  const {
    selectedWellName,
    appliedHierarchyName,
    appliedRiskMitigationId,
    riskMitigationQueryResult,
  } = useGlobalFiltersContext();

  const { riskMitigations } = riskMitigationQueryResult.data ?? {};

  const riskMitigationName =
    riskMitigations?.find(
      (mitigation) => mitigation?.id === appliedRiskMitigationId,
    )?.value ?? "";

  const {
    mitigationRiskByThreatSeries,
    mitigationRiskByConsequenceSeries,
    mitigationRiskByOutcomeScenarioSeries,
    mitigationRiskDataKeys,
  } = useMemo(() => {
    let mitigationRiskByThreatSeries: Array<GeneralBarDatum> = [];
    let mitigationRiskByConsequenceSeries: Array<GeneralBarDatum> = [];
    let mitigationRiskByOutcomeScenarioSeries: Array<GeneralBarDatum> = [];

    if (overallRiskChartsQueryResult.data?.riskManagementChartsForWellNode) {
      const {
        overallRiskByThreat,
        overallRiskByConsequence,
        overallRiskByOutcomeScenario,
      } = overallRiskChartsQueryResult.data.riskManagementChartsForWellNode;

      mitigationRiskByThreatSeries =
        convertPostMitigationBarDataToSeries(overallRiskByThreat);

      mitigationRiskByConsequenceSeries = convertPostMitigationBarDataToSeries(
        overallRiskByConsequence,
      );

      mitigationRiskByOutcomeScenarioSeries =
        convertPostMitigationBarDataToSeries(
          overallRiskByOutcomeScenario,
          "inTotal",
        );

      const tooltipTitle = selectedWellName?.label
        ? `${selectedWellName.label} (${appliedHierarchyName})`
        : "";

      const baselineTotal = overallRiskByThreat.reduce(
        (acc, curr) => acc + (curr.opTotal ?? 0),
        0,
      );

      const postMitigationTotal = overallRiskByThreat.reduce(
        (acc, curr) => acc + (curr.postMitigation ?? 0),
        0,
      );

      for (const threatDatum of mitigationRiskByThreatSeries) {
        threatDatum.title = tooltipTitle;
        threatDatum.formatter = (value: number) =>
          storageFormatters.currency(value);
        appendPostMitigationRiskTooltipExtras(
          riskMitigationName,
          baselineTotal,
          postMitigationTotal,
          threatDatum,
        );
      }

      for (let i = 0; i < mitigationRiskByConsequenceSeries.length; i++) {
        mitigationRiskByConsequenceSeries[i].title = tooltipTitle;
        mitigationRiskByConsequenceSeries[i].formatter = (value: number) =>
          storageFormatters.currency(value);

        appendPostMitigationSubgroups(
          [overallRiskByConsequence[i]],
          mitigationRiskByConsequenceSeries[i],
          "Consequence Type by Category",
        );

        appendPostMitigationRiskTooltipExtras(
          riskMitigationName,
          baselineTotal,
          postMitigationTotal,
          mitigationRiskByConsequenceSeries[i],
        );
      }

      for (let i = 0; i < mitigationRiskByOutcomeScenarioSeries.length; i++) {
        mitigationRiskByOutcomeScenarioSeries[i].title = tooltipTitle;
        mitigationRiskByOutcomeScenarioSeries[i].formatter = (value: number) =>
          storageFormatters.currency(value);
        appendPostMitigationRiskTooltipExtras(
          riskMitigationName,
          baselineTotal,
          postMitigationTotal,
          mitigationRiskByOutcomeScenarioSeries[i],
        );
        mitigationRiskByOutcomeScenarioSeries[i].tooltipExtras =
          mitigationRiskByOutcomeScenarioSeries[i].tooltipExtras?.concat([
            {
              label: `Baseline LOF (${overallRiskByOutcomeScenario[i].group})`,
              value: storageFormatters.scientificNotation(
                overallRiskByOutcomeScenario[i]?.opLof ?? 0,
              ),
              subGroup: "Baseline",
            },
            {
              label: `Post-Mitigation LOF (${overallRiskByOutcomeScenario[i].group})`,
              value: storageFormatters.scientificNotation(
                overallRiskByOutcomeScenario[i]?.inLof ?? 0,
              ),
              subGroup: "Post-Mitigation",
            },
            {
              label: `Baseline COF (${overallRiskByOutcomeScenario[i].group})`,
              value: storageFormatters.currency(
                overallRiskByOutcomeScenario[i]?.opCof ?? 0,
              ),
              subGroup: "Baseline",
            },
            {
              label: `Post-Mitigation COF (${overallRiskByOutcomeScenario[i].group})`,
              value: storageFormatters.currency(
                overallRiskByOutcomeScenario[i]?.inCof ?? 0,
              ),
              subGroup: "Post-Mitigation",
            },
          ]);
      }

      return {
        mitigationRiskByThreatSeries,
        mitigationRiskByConsequenceSeries,
        mitigationRiskByOutcomeScenarioSeries,
        mitigationRiskDataKeys: ["Baseline", "Post-Mitigation"],
      };
    }

    return {
      mitigationRiskByThreatSeries: [],
      mitigationRiskByConsequenceSeries: [],
      mitigationRiskByOutcomeScenarioSeries: [],
      mitigationRiskDataKeys: [],
    };
  }, [
    overallRiskChartsQueryResult.data?.riskManagementChartsForWellNode,
    selectedWellName,
    appliedHierarchyName,
    riskMitigationName,
  ]);

  return {
    overallRiskChartsQueryResult,
    mitigationRiskByThreatSeries,
    mitigationRiskByConsequenceSeries,
    mitigationRiskByOutcomeScenarioSeries,
    mitigationRiskDataKeys,
  };
};
